import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { Image, Spinner } from 'ui'
import { snackBar } from 'store'

import Close1Icon from 'assets/icons/ims/Close2.svg'

import { changeDefaultCommunity, fetchCommunityOptions } from './defaultCommunityApi'
import { CommunityOption, limitToMemberships } from './defaultCommunity'

const DialogBody = styled(DialogContent)`
  background: white;
  border-top-width: 1px;
`
const BodyContainer = styled.div`
  display: flex;
  width: 520px;
  max-width: 100%;
  padding: 23.991px 23.991px 0 23.991px;
  flex-direction: column;
  align-items: flex-start;
  gap: 15.994px;
  border-radius: 8px;
  background: #FFF;
`
const TopContainer = styled.div`
  display: flex;
  padding-right: 0;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 15.994px;
  align-self: stretch;
`
const TopHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 7.997px;
  flex: 1 0 0;
`
const Title = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  color: #0F172A;
  font-family: Rubik;
  font-size: 18px;
  font-weight: 500;
  line-height: 27px;
`
const Subtitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  color: #64748B;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 400;
  line-height: 23px;
`
const Note = styled.div`
  align-self: stretch;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #FCD34D;
  background: #FEF3C7;
  color: #92400E;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 400;
  line-height: 19px;
`
const FieldLabel = styled.div`
  align-self: stretch;
  color: #334155;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
`
const PickerRow = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  gap: 10px;
`
const StyledAutocomplete = styled(Autocomplete)`
  flex: 1 0 0;
`
const Inactive = styled.span`
  margin: 0 6px;
  color: #DC2626;
  font-size: 12px;
`
const BottomContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 11.989px;
  align-self: stretch;
  padding-bottom: 16px;
`
const CancelButton = styled(Button)`
  && {
    display: flex;
    height: 40px;
    padding: 7px 16px;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    background: #F1F5F9;
    color: #0F172A;
    font-family: Rubik;
    font-size: 16px;
    font-weight: 500;
  }
`
const ConfirmButton = styled(Button)`
  && {
    display: flex;
    height: 40px;
    min-width: 128px;
    padding: 7px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 6px;
    background: #2563EB;
    color: #FFF;
    font-family: Rubik;
    font-size: 16px;
    font-weight: 500;
  }
  &&:hover {
    background: #1D4ED8;
  }
  &&.Mui-disabled {
    background: #BFDBFE;
    color: #F8FAFC;
  }
`
const CloseButton = styled(Button)`
  && {
    min-width: 0;
    padding: 0;
  }
`
const CloseIcon = styled(Image)`
  width: 27.983px;
  height: 27.983px;
  padding: 4px;
`

type Props = {
  open: boolean
  contactId: number
  contactName?: string
  currentDefaultCommunityId: number | string | null | undefined
  // The communities the contact belongs to. A primary community they are not a
  // member of is not offered - the picker only reorders what they already have.
  memberCommunityIds: (number | string)[] | null | undefined
  onClose: () => void
  // The primary community the server confirmed, so the form behind this dialog
  // can relock or unlock the contact's global fields without re-reading them.
  onChanged: (defaultCommunityId: number, name?: string) => void
}

const DefaultCommunityModal = ({
  open,
  contactId,
  contactName,
  currentDefaultCommunityId,
  memberCommunityIds,
  onClose,
  onChanged,
}: Props) => {
  const { t } = useTranslation('members')

  const [communities, setCommunities] = useState<CommunityOption[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState<CommunityOption | null>(null)
  // The community list is the same for every contact, so it is read once per
  // mount rather than on each open of the dialog.
  const loadedRef = useRef(false)

  useEffect(() => {
    if (!open || loadedRef.current) {
      return
    }
    loadedRef.current = true
    setLoading(true)

    fetchCommunityOptions()
      .then(communities => {
        setCommunities(communities)
        // An empty list is the api layer's answer to a failed read - it reports
        // the error itself and hands back nothing - so the marker is cleared the
        // same way as for a thrown one, letting the next open try again instead
        // of showing an empty picker for as long as the population tab is open.
        if (!communities.length) {
          loadedRef.current = false
        }
      })
      .catch(() => {
        loadedRef.current = false
        snackBar.showError(t`population:communities_load_error`)
      })
      .finally(() => setLoading(false))
  }, [open])

  // The admin list is read whole - it is the only place the names come from -
  // and narrowed here to the contact's own communities.
  const options = useMemo(
    () => limitToMemberships(communities, memberCommunityIds, currentDefaultCommunityId),
    [communities, memberCommunityIds, currentDefaultCommunityId],
  )

  // Opened on the contact's current primary community, so the picker says where
  // they belong now and a mistaken open can be closed without changing anything.
  useEffect(() => {
    if (!open) {
      return
    }
    setSelected(options.find(o => Number(o.id) === Number(currentDefaultCommunityId)) ?? null)
  }, [open, options, currentDefaultCommunityId])

  const currentName = options.find(o => Number(o.id) === Number(currentDefaultCommunityId))?.name

  const handleConfirm = async () => {
    if (!selected || !contactId) {
      return
    }

    setSaving(true)
    try {
      const res = await changeDefaultCommunity(contactId, selected.id)

      if (!res.ok) {
        // Cast because the translation helper is typed as possibly returning a
        // non-string, which the snackbar does not accept.
        const message = (
          res.status === 403
            ? t`population:default_community_forbidden`
            : res.code === 'USER_NOT_FOUND'
            ? t`population:default_community_user_not_found`
            : res.code === 'COMMUNITY_NOT_FOUND'
            ? t`population:default_community_not_found`
            : res.message || t`population:default_community_change_error`
        ) as string
        snackBar.showError(message)
        return
      }

      snackBar.showSuccess(t('population:default_community_changed', { community: selected.name }))
      onChanged(res.defaultCommunityId, selected.name)
      onClose()
    } catch (e: any) {
      snackBar.showError(e?.message || t`population:default_community_change_error`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog style={{ zIndex: 1300 }} open={open} maxWidth={'sm'}>
      <DialogBody>
        <BodyContainer>
          <TopContainer>
            <TopHeader>
              <Title>{t`population:default_community_title`}</Title>
              <Subtitle>
                {contactName
                  ? t('population:default_community_subtitle_named', { name: contactName })
                  : t`population:default_community_subtitle`}
              </Subtitle>
              {currentName && (
                <Subtitle>{t('population:current_default_community', { community: currentName })}</Subtitle>
              )}
            </TopHeader>
            <CloseButton onClick={onClose}>
              <CloseIcon src={Close1Icon} />
            </CloseButton>
          </TopContainer>

          <Note>{t`population:default_community_note`}</Note>

          {!loading && options.length <= 1 && (
            <Subtitle>{t`population:default_community_single_membership`}</Subtitle>
          )}

          <FieldLabel>{t`population:choose_community`}</FieldLabel>
          <PickerRow>
            <StyledAutocomplete
              options={options as any}
              value={selected as any}
              loading={loading}
              disabled={loading || saving}
              onChange={(e: any, v: any) => setSelected(v)}
              getOptionLabel={(o: any) => o?.name || ''}
              getOptionSelected={(o: any, v: any) => Number(o?.id) === Number(v?.id)}
              openOnFocus
              selectOnFocus
              noOptionsText={t`population:no_communities`}
              renderOption={(o: any) => (
                <span>
                  {o.name}
                  {!o.isActive && <Inactive>{t`app:inactive`}</Inactive>}
                </span>
              )}
              renderInput={(p: any) => (
                <TextField
                  {...p}
                  variant='outlined'
                  size='small'
                  placeholder={t`population:choose`}
                />
              )}
            />
            {loading && <Spinner size={16} />}
          </PickerRow>

          <BottomContainer>
            <CancelButton onClick={onClose} disabled={saving}>{t`population:cancel_action`}</CancelButton>
            <ConfirmButton
              onClick={handleConfirm}
              disabled={
                saving ||
                loading ||
                !selected ||
                Number(selected.id) === Number(currentDefaultCommunityId)
              }
              startIcon={saving ? <Spinner size={16} /> : undefined}
            >
              {t`population:save`}
            </ConfirmButton>
          </BottomContainer>
        </BodyContainer>
      </DialogBody>
    </Dialog>
  )
}

export default observer(DefaultCommunityModal)
